























import { Component, Prop, Vue } from "vue-property-decorator";
import CzButton from "../cz/CzButton.vue";
import CzNotice from "../cz/CzNotice.vue";
import { GuidelineApi } from "./guidelineApi";
import { uploadToBunny } from "../videoSubmission/bunnyTusUpload";

const AskVideo = 1;
const Reviewed = 3;
const WellDone = 1;
const MaxSeconds = 2 * 60;
const MaxPhotoSide = 1600;
const PollMs = 5000;
const PollMaxMs = 15 * 60000;

function videoSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration || 0);
    };
    video.onerror = () => resolve(0);
    video.src = URL.createObjectURL(file);
  });
}

// Drawn again on a canvas, the photo keeps its pixels (upright) and loses what the camera wrote in it: place, date, device.
async function cleanPhoto(file: File): Promise<Blob | null> {
  try {
    const image = await createImageBitmap(file);
    const scale = Math.min(1, MaxPhotoSide / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    (canvas.getContext("2d") as CanvasRenderingContext2D).drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  } catch (e) {
    return null;
  }
}

// One place of a guideline that asks for a video or a photo (the guideline itself or one session): the last thing sent,
// its review, and the button to send it, or to send it again after "Correct".
@Component({ components: { CzButton, CzNotice } })
export default class GuidelineSubmission extends Vue {
  @Prop({ required: true }) guidelineId!: number;
  @Prop({ required: true }) place!: any;

  uploading = false;
  percent = 0;
  error = "";
  photoUrl = "";
  timer: any = null;

  get s(): any {
    return this.place.submission;
  }

  get isVideo(): boolean {
    return this.place.kind === AskVideo;
  }

  get wellDone(): boolean {
    return !!this.s && this.s.status === Reviewed && this.s.result === WellDone;
  }

  get askText(): string {
    return this.isVideo
      ? this.$t("Your professional wants to see how you do it: record a video of up to 2 minutes").toString()
      : this.$t("Your professional wants to see how it goes: take a photo").toString();
  }

  get actionText(): string {
    if (this.s) return this.isVideo ? this.$t("Send another video").toString() : this.$t("Send another photo").toString();
    return this.isVideo ? this.$t("Send a video").toString() : this.$t("Send a photo").toString();
  }

  get stateText(): string {
    if (this.s.status === Reviewed) return this.wellDone ? this.$t("Well done").toString() : this.$t("To correct: look at the comments and send it again").toString();
    if (this.s.rejected) return this.$t("The video could not be used: send another one of up to 2 minutes").toString();
    if (this.s.mediaDeleted) return this.$t("It was not reviewed in time: you can send it again").toString();
    return this.$t("Sent. Your professional will review it").toString();
  }

  async mounted() {
    if (this.s && this.s.pendingVideoId) this.watchVideo(this.s.pendingVideoId, Date.now());
    if (this.s && this.s.hasPhoto) {
      const blob = await GuidelineApi.photo(this.s.id);
      if (blob) this.photoUrl = URL.createObjectURL(blob);
    }
  }

  beforeDestroy() {
    clearTimeout(this.timer);
    if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
  }

  seekTo: number | null = null;

  // Touching a comment's second plays the video from there (D22), as in Formaciones.
  get videoUrl(): string {
    if (this.seekTo == null) return this.s.playUrl;
    const base = this.s.playUrl.replace(/[&?]t=\d+/g, "");
    return base + (base.includes("?") ? "&" : "?") + "t=" + this.seekTo;
  }

  second(s: number): string {
    return Math.floor(s / 60) + ":" + ("0" + (s % 60)).slice(-2) + " ";
  }

  pick() {
    this.error = "";
    (this.$refs.file as HTMLInputElement).click();
  }

  async picked(e: any) {
    const file: File = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (this.isVideo) await this.sendVideo(file);
    else await this.sendPhoto(file);
  }

  async sendVideo(file: File) {
    if ((await videoSeconds(file)) > MaxSeconds + 1) {
      this.error = this.$t("The video can last 2 minutes at most").toString();
      return;
    }
    this.uploading = true;
    this.percent = 0;
    let upload: any = null;
    let sent: any = null;
    try {
      const created = await GuidelineApi.createVideo(this.guidelineId, this.place.sessionId);
      upload = created && created.ok && created.model ? created.model.upload : null;
      const uploaded = !!upload && (await uploadToBunny(file, upload, (p) => (this.percent = p)));
      sent = !upload ? created : uploaded ? await GuidelineApi.submitVideo(this.guidelineId, this.place.sessionId, upload.videoId) : null;
    } catch {
      sent = null;
    } finally {
      this.uploading = false;
    }
    if (!sent || !sent.ok) {
      this.error = (sent && sent.errorMsg) || this.$t("The video could not be uploaded").toString();
      return;
    }
    this.$emit("changed");
    this.watchVideo(upload.videoId, Date.now());
  }

  async sendPhoto(file: File) {
    const photo = await cleanPhoto(file);
    if (!photo) {
      this.error = this.$t("Send a JPG or PNG picture").toString();
      return;
    }
    this.uploading = true;
    this.percent = 100;
    const sent = await GuidelineApi.submitPhoto(this.guidelineId, this.place.sessionId, photo).catch(() => null);
    this.uploading = false;
    if (!sent || !sent.ok) {
      this.error = (sent && sent.errorMsg) || this.$t("The picture could not be sent").toString();
      return;
    }
    this.$emit("changed");
  }

  // Until Bunny has processed it: a video over two minutes, or one Bunny could not process, comes back rejected.
  watchVideo(videoId: string, started: number) {
    this.timer = setTimeout(async () => {
      const res = await GuidelineApi.videoStatus(videoId).catch(() => null);
      const status = res && !res.rateLimited ? res.model : null;
      if (status && (status.ready || status.rejected)) {
        this.$emit("changed");
        return;
      }
      if (Date.now() - started < PollMaxMs) this.watchVideo(videoId, started);
    }, PollMs);
  }
}
